// Review Center — reconcile the agency's customizations after we publish new content over them.
// Each card shows what they customized ("yours") next to what changed ("ours, updated"), then lets
// them Keep (re-affirm), Edit (revise + re-affirm), or Discard (revert to our default).
import { useEffect, useState } from 'react';
import { agencyApi, type ReviewItem } from '../../lib/agencyApi';
import { allTours } from '../../widget/tours/index.js';
import { Button, Card, Chip, EmptyNote, ErrorNote, SectionHeader, Spinner } from './ui';

type StepText = { title: string; description: string };
function masterSteps(tourId: string): StepText[] {
  const t = (allTours as Record<string, { steps?: Array<{ popover?: { title?: string; description?: string } }> }>)[tourId];
  return (t?.steps ?? []).map((s) => ({
    title: (s.popover?.title ?? '').trim(),
    description: (s.popover?.description ?? '').replace(/\s+/g, ' ').trim(),
  }));
}
function tourName(tourId: string, fallback: string): string {
  const t = (allTours as Record<string, { name?: string; displayName?: string }>)[tourId];
  return t?.displayName || t?.name || fallback;
}

export default function ReviewCenter({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const [items, setItems] = useState<ReviewItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try { const q = await agencyApi.reviewQueue(); setItems(q); onCountChange?.(q.length); }
    catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { void load(); }, []);

  if (error) return <ErrorNote error={error} />;
  if (!items) return <Spinner />;

  return (
    <div className="max-w-3xl space-y-4">
      <SectionHeader
        title="Review Center"
        subtitle="When we publish an update to an article or tour you've customized, reconcile it here — keep your version, revise it, or revert to ours."
      />
      {items.length === 0 ? (
        <EmptyNote>Nothing to review right now. If we update content you've customized, it'll show up here.</EmptyNote>
      ) : (
        items.map((it) => <ReviewCard key={it.id} item={it} onResolved={load} />)
      )}
    </div>
  );
}

function ReviewCard({ item, onResolved }: { item: ReviewItem; onResolved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<null | 'keep' | 'update' | 'discard'>(null);
  const [error, setError] = useState<string | null>(null);

  // Editable drafts.
  const [note, setNote] = useState(item.content_type === 'doc' ? item.payload : '');
  const [overrides, setOverrides] = useState<Record<string, { title?: string; description?: string }>>(() => {
    if (item.content_type !== 'tour') return {};
    try { return JSON.parse(item.payload) as Record<string, { title?: string; description?: string }>; } catch { return {}; }
  });

  async function resolve(action: 'keep' | 'update' | 'discard') {
    setBusy(action); setError(null);
    try {
      const payload = action === 'update' ? (item.content_type === 'doc' ? note : overrides) : undefined;
      await agencyApi.resolveReview(item.id, action, payload);
      onResolved();
    } catch (e) { setError((e as Error).message); setBusy(null); }
  }

  const isDoc = item.content_type === 'doc';
  const steps = isDoc ? [] : masterSteps(item.asset_id);
  const overriddenIdx = isDoc ? [] : Object.keys(overrides).map(Number).filter((n) => !Number.isNaN(n)).sort((a, b) => a - b);

  const setStep = (i: number, field: 'title' | 'description', value: string) => {
    setOverrides((prev) => {
      const next = { ...prev };
      const cur = { ...(next[i] ?? {}) };
      if (value.trim()) cur[field] = value; else delete cur[field];
      if (Object.keys(cur).length) next[i] = cur; else delete next[i];
      return next;
    });
  };

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span aria-hidden>{isDoc ? '📄' : '🧭'}</span>
            <span className="truncate font-medium text-slate-800">{isDoc ? item.asset_title : tourName(item.asset_id, item.asset_title)}</span>
            <Chip label={isDoc ? 'Article note' : 'Tour text'} muted />
          </div>
          <div className="mt-1 text-sm text-slate-500">
            We published {item.new_version ? `version ${item.new_version}` : 'a new version'} of this {isDoc ? 'article' : 'tour'}.
            Your customization was based on {item.base_master_version === 'bundle' ? 'the original' : `version ${item.base_master_version}`} — review and reconcile.
          </div>
        </div>
      </div>

      {error && <ErrorNote error={error} />}

      {isDoc ? (
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Your note</div>
            {editing ? (
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={5}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
              />
            ) : (
              <div className="whitespace-pre-wrap rounded-lg border-l-2 border-brand bg-slate-50 px-3 py-2 text-sm text-slate-700">{item.payload || <span className="text-slate-400">(empty)</span>}</div>
            )}
          </div>
          {item.article_html && (
            <details className="text-sm">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-700">View the updated article</summary>
              <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-slate-200 p-3 text-sm text-slate-600" dangerouslySetInnerHTML={{ __html: item.article_html }} />
            </details>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {overriddenIdx.length === 0 ? (
            <p className="text-sm text-slate-400">No step overrides remain.</p>
          ) : (
            overriddenIdx.map((i) => {
              const ours = steps[i] ?? { title: '', description: '' };
              const o = overrides[i] ?? {};
              return (
                <div key={i} className="rounded-lg border border-slate-200 p-3">
                  <div className="mb-2 text-xs font-semibold text-slate-500">Step {i + 1}</div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <div className="mb-1 text-xs uppercase tracking-wide text-slate-400">Ours (updated)</div>
                      <div className="rounded bg-slate-50 px-2 py-1.5 text-sm font-medium text-slate-700">{ours.title || <span className="text-slate-400">—</span>}</div>
                      {ours.description && <div className="mt-1 rounded bg-slate-50 px-2 py-1.5 text-xs text-slate-500">{ours.description}</div>}
                    </div>
                    <div>
                      <div className="mb-1 text-xs uppercase tracking-wide text-slate-400">Yours</div>
                      {editing ? (
                        <>
                          <input value={o.title ?? ''} onChange={(e) => setStep(i, 'title', e.target.value)} placeholder={ours.title || 'Title'} className="mb-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-brand" />
                          <textarea value={o.description ?? ''} onChange={(e) => setStep(i, 'description', e.target.value)} placeholder={ours.description || 'Description'} rows={2} className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-brand" />
                        </>
                      ) : (
                        <>
                          <div className="rounded bg-sky-50 px-2 py-1.5 text-sm font-medium text-slate-700">{o.title || <span className="text-slate-400">(uses ours)</span>}</div>
                          {o.description && <div className="mt-1 rounded bg-sky-50 px-2 py-1.5 text-xs text-slate-500">{o.description}</div>}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        {editing ? (
          <>
            <Button onClick={() => resolve('update')} disabled={!!busy}>{busy === 'update' ? 'Saving…' : 'Save & keep'}</Button>
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={!!busy}>Cancel</Button>
          </>
        ) : (
          <>
            <Button onClick={() => resolve('keep')} disabled={!!busy}>{busy === 'keep' ? 'Keeping…' : 'Keep mine'}</Button>
            <Button variant="secondary" onClick={() => setEditing(true)} disabled={!!busy}>Edit</Button>
            <Button variant="ghost" onClick={() => resolve('discard')} disabled={!!busy}>{busy === 'discard' ? 'Discarding…' : 'Discard (use ours)'}</Button>
          </>
        )}
      </div>
    </Card>
  );
}
